// src/admin/SectionsList.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import SectionEditor from "./SectionEditor";

export default function SectionsList({
  baseURL = "https://gantasoft.in/rankridgecollege/dev/v1",
  apiKey = "12345678",
  pageId,     // optional filter
  subpageId,  // optional filter
}) {
  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: { "X-API-Key": apiKey },
      }),
    [baseURL, apiKey]
  );

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editorItem, setEditorItem] = useState(null); // null or object

  async function load() {
    setLoading(true);
    try {
      const params = {
        q, status, page, limit,
        sort: "sort_order", order: "ASC",
      };
      if (pageId) params.page_id = pageId;
      if (subpageId) params.subpage_id = subpageId;
      const { data } = await api.get("/sections");
      setRows(data?.items || []);
      setTotal(data?.pagination?.total || 0);
      setPages(data?.pagination?.pages || 0);
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, page, pageId, subpageId]);

  const toggleStatus = async (it) => {
    const next = it.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/sections/${it.section_id}`, { field: "status", value: next });
      setRows(arr => arr.map(r => r.section_id === it.section_id ? { ...r, status: next } : r));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Status change failed");
    }
  };

  const destroy = async (it) => {
    if (!window.confirm("Delete this section?")) return;
    try {
      await api.delete(`/sections/${it.section_id}`);
      setRows(arr => arr.filter(r => r.section_id !== it.section_id));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex flex-wrap items-end gap-2 justify-between">
        <h2 className="text-xl font-semibold">Sections</h2>
        <button
          onClick={() => setEditorItem({
            section_id: null,
            page_id: pageId || null,
            subpage_id: subpageId || null,
            section_title: "",
            section_content: "",
            section_image: "",
            sort_order: 0,
            status: "active",
          })}
          className="px-3 py-1.5 bg-green-700 text-white rounded"
        >
          + Add Section
        </button>
      </div>

      <div className="mt-4 grid md:grid-cols-4 gap-2">
        <input
          placeholder="Search title/content..."
          className="border rounded px-3 py-2"
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
        />
        <select
          className="border rounded px-3 py-2"
          value={status}
          onChange={(e) => { setPage(1); setStatus(e.target.value); }}
        >
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
          <option value="deleted">deleted</option>
        </select>
      </div>

      {loading ? (
        <div className="mt-4 text-gray-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="mt-4 text-gray-500">No results.</div>
      ) : (
        <div className="mt-4 grid gap-3">
          {rows.map((it) => (
            <div key={it.section_id} className="border rounded p-3 bg-white">
              <div className="flex items-center justify-between">
                <div className="font-medium">{it.section_title}</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{it.status}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100">Sort: {it.sort_order ?? 0}</span>
                </div>
              </div>
              {it.section_image && (
                <div className="mt-2 aspect-video bg-gray-50 border rounded overflow-hidden">
                  <img src={"https://gantasoft.in/rankridgeschool/dev/"+it.section_image} alt={it.section_title} className="w-full h-full object-cover" />
                </div>
              )}
              {it.section_content && (
                <div className="text-sm text-gray-700 mt-2 line-clamp-3">{it.section_content}</div>
              )}
              <div className="text-xs text-gray-500 mt-2">
                ID: {it.section_id} · Page: {it.page_id ?? "-"} · Subpage: {it.subpage_id ?? "-"}
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => toggleStatus(it)} className="px-2 py-1 text-xs rounded bg-gray-800 text-white">
                  {it.status === "active" ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => setEditorItem(it)} className="px-2 py-1 text-xs rounded bg-blue-600 text-white">
                  Edit
                </button>
                <button onClick={() => destroy(it)} className="px-2 py-1 text-xs rounded bg-red-600 text-white">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1.5 rounded bg-gray-200 disabled:opacity-50"
          >
            Prev
          </button>
          <div className="text-sm">Page {page} of {pages} · {total} total</div>
          <button
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            disabled={page >= pages}
            className="px-3 py-1.5 rounded bg-gray-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {editorItem && (
        <SectionEditor
          baseURL={baseURL}
          apiKey={apiKey}
          initial={editorItem}
          onClose={() => setEditorItem(null)}
          onSaved={() => { setEditorItem(null); load(); }}
        />
      )}
    </div>
  );
}
